/**
 * API client for the Oppslag module (#472).
 *
 * Every call here costs SVV quota, so each response echoes the caller's
 * remaining daily budget and the UI keeps it on screen.
 *
 * Note that all four lookups are POST, including the person one. That is not
 * REST pedantry: the backend's request logger persists query parameters, so a
 * fødselsnummer must travel in a body, never in a URL.
 */
import api from '../../services/api'

export interface KvoteStatus {
  budsjett: number
  brukt: number
  gjenstaende: number
}

export interface Kvote {
  dato: string
  teknisk: KvoteStatus
  maskinporten: KvoteStatus
  brreg: KvoteStatus
}

export interface HeftelsePart {
  navn: string | null
  part_type: 'person' | 'organisasjon'
  organisasjonsnummer: string | null
  rolle: string | null
  rolle_tekst: string | null
  adresselinje1: string | null
  postnummer: string | null
  poststed: string | null
  kommunenavn: string | null
}

export interface Heftelse {
  dokumentnummer: string | null
  type_kode: string | null
  type: string | null
  /** 'finansiering' | 'tvang' | 'annet'. Keyed on the type code, never on the
   *  free-text description. A salgspant is a car loan; an utleggspant is the
   *  namsmann acting on an unpaid debt, and the two must not look alike. */
  kategori: 'finansiering' | 'tvang' | 'annet'
  status: string | null
  /** Only a tinglyst lien actually encumbers the vehicle. */
  aktiv: boolean
  tinglyst: string | null
  belop: number | null
  valuta: string | null
  krav_type: string | null
  rettighetshaver: HeftelsePart | null
  /** 'Panthaver' / 'Eier' / 'Saksøker' — the party holding the right is
   *  called something different per lien type, so the label travels with it. */
  rettighetshaver_etikett: string
  /** Plural: a salgspant routinely names co-borrowers. */
  forpliktede: HeftelsePart[]
  forpliktet_etikett: string
  namsmyndighet: string | null
  prosessfullmektig: string | null
  kjoretoy: string[]
  antall_kjoretoy: number
  /** Non-empty means this lien also covers other vehicles, and the amount is
   *  therefore NOT this car's debt alone. */
  andre_kjoretoy: string[]
}

export interface HeftelseTreff {
  sokt: string
  kjennemerke: string | null
  heftelser: Heftelse[]
  antall_aktive: number
  oppslagstidspunkt: string | null
}

export interface HeftelseResponse {
  treff: HeftelseTreff[]
  feil: Feil[]
  kvote: Kvote
}

export interface Feil {
  sokt?: string | null
  kode: string
}

export interface Eier {
  eier_type: 'person' | 'organisasjon'
  navn: string | null
  organisasjonsnummer: string | null
  // No fodselsdato. SVV returns a birthdate for private owners; the backend
  // drops it in app/services/svv/mapping.py before it reaches any response,
  // so there is nothing here to render even by accident.
  adresselinje1: string | null
  adresselinje2: string | null
  postnummer: string | null
  poststed: string | null
  kommunenavn: string | null
  land: string | null
  registrert_pa_eier: string | null
  vedtakstidspunkt: string | null
}

export interface Skilt {
  kjennemerke: string | null
  kategori: string | null
  type: string | null
  fom: string | null
  til: string | null
}

export interface DekkFelg {
  /** SVV returns one combination per approved fitment — a summer and a winter
   *  set appear as two combinations over the same axles, so rows are grouped
   *  by this rather than flattened (which would drop the winter set). */
  kombinasjon: number
  aksel: number | null
  dekkdimensjon: string | null
  felgdimensjon: string | null
  belastningskode: string | null
  hastighetskode: string | null
  innpress: string | null
}

/** Everything the teknisk response carries beyond the table's nine columns.
 *  Values are whatever SVV had — a field absent for this vehicle comes back
 *  null, and the detail panel omits the line rather than printing a dash. */
export interface Detaljer {
  registrering: Record<string, string | null>
  godkjenning: Record<string, string | boolean | null>
  motor: Record<string, string | number | boolean | null>
  miljo: Record<string, string | number | boolean | null>
  vekter: Record<string, number | null>
  karosseri: Record<string, string | number | boolean | null>
  dekk_og_felg: DekkFelg[]
  merknader: string[]
}

export interface KjoretoyRad {
  sokt?: string | null
  /** Set when the search hit a plate other than the vehicle's current one —
   *  typically a personalised plate. Must be shown, or the row looks like it
   *  belongs to a plate the user never typed. */
  matchet_pa?: string | null
  kjennemerke: string | null
  understellsnummer: string | null
  kuid: string | null
  merke?: string | null
  handelsbetegnelse?: string | null
  fabrikant?: string | null
  typebetegnelse?: string | null
  kjoretoygruppe?: string | null
  registrert_forstegang?: string | null
  registreringsstatus?: string | null
  registreringsstatus_tekst?: string | null
  registrert_forstegang_pa_eier?: string | null
  pkk_kontrollfrist?: string | null
  pkk_sist_godkjent?: string | null
  drivstoff?: string | null
  euro_klasse?: string | null
  girkasse?: string | null
  karosseri?: string | null
  egenvekt?: number | null
  tillatt_totalvekt?: number | null
  kjennemerker?: Skilt[]
  eier?: Eier | null
  /** Registered co-owner. Roughly 1 vehicle in 18 has one, and that one must
   *  also sign a sale — so this is shown prominently, not tucked away. */
  medeier?: Eier | null
  /** Lessee. An organisation here means næringsleasing, a person means
   *  privatleasing. */
  leasingtaker?: Eier | null
  detaljer?: Detaljer
}

export interface TekniskResponse {
  treff: KjoretoyRad[]
  feil: Feil[]
  kvote: Kvote
}

export interface KjoretoyResponse {
  treff: KjoretoyRad[]
  feil: Feil[]
  kvote: Kvote
  svv_gjenstaende_kvote: number | null
}

export interface EierLedd {
  steg: number
  eier: Eier | null
  /** The pair registered at THIS step's instant, not today's. */
  medeier?: Eier | null
  leasingtaker?: Eier | null
  registrert_pa_eier: string | null
  dtg: string | null
}

export interface EierhistorikkResponse {
  kjennemerke: string | null
  kuid: string | null
  kjoretoy: KjoretoyRad | null
  historikk: EierLedd[]
  /** 'ingen_flere_eiere' | 'maks_steg' | 'tidligst' | 'feilmelding' | 'kvote'.
   *  A chain that stopped at maks_steg is NOT a complete history and the UI
   *  says so rather than letting it look like one. */
  stoppet_fordi: string
  kvote: Kvote
  svv_gjenstaende_kvote: number | null
}

export interface PersonSnapshot {
  dtg: string | null
  dato: string
  kjoretoy: KjoretoyRad[]
}

export interface PersonResponse {
  etternavn: string
  snapshots: PersonSnapshot[]
  feil: Feil[]
  stoppet_pga_kvote: boolean
  kvote: Kvote
  svv_gjenstaende_kvote: number | null
}

export async function hentKvote(): Promise<Kvote> {
  const { data } = await api.get<Kvote>('/oppslag/kvote')
  return data
}

export async function tekniskOppslag(sok: string[]): Promise<TekniskResponse> {
  const { data } = await api.post<TekniskResponse>('/oppslag/teknisk', { sok })
  return data
}

export type KjoretoyNokkel = 'kjennemerker' | 'understellsnummer' | 'kuid'

export async function kjoretoyOppslag(
  nokkel: KjoretoyNokkel,
  verdier: string[],
  dtg?: string,
): Promise<KjoretoyResponse> {
  const { data } = await api.post<KjoretoyResponse>('/oppslag/kjoretoy', {
    [nokkel]: verdier,
    ...(dtg ? { dtg } : {}),
  })
  return data
}

/**
 * Walk ownership backwards.
 *
 * Pass `start` when the caller already knows the vehicle's kuid and its
 * current owner's start date — Leads does, from SVVState. The walk then skips
 * the plate lookup it would otherwise spend its first unit on, and its first
 * query already lands on the previous owner. The two fields go together; the
 * backend answers 422 for one without the other.
 */
export async function eierhistorikk(
  kjennemerke: string,
  maksSteg = 10,
  start?: { kuid: string; fraDato: string },
): Promise<EierhistorikkResponse> {
  const { data } = await api.post<EierhistorikkResponse>(
    '/oppslag/kjoretoy/eierhistorikk',
    {
      kjennemerke,
      maks_steg: maksSteg,
      ...(start ? { kuid: start.kuid, fra_dato: start.fraDato } : {}),
    },
  )
  return data
}

export async function personOppslag(
  fodselsnummer: string,
  etternavn: string,
  aarTilbake: number,
): Promise<PersonResponse> {
  const { data } = await api.post<PersonResponse>('/oppslag/person', {
    fodselsnummer,
    etternavn,
    aar_tilbake: aarTilbake,
  })
  return data
}

export async function heftelseOppslag(
  kjennemerker: string[],
): Promise<HeftelseResponse> {
  const { data } = await api.post<HeftelseResponse>('/oppslag/heftelser', {
    kjennemerker,
  })
  return data
}

/** Split a pasted blob into search terms. Users paste from Excel, so newlines,
 *  commas, semicolons and tabs all have to work as separators. */
export function parseSokeliste(raw: string): string[] {
  return raw
    .split(/[\n,;\t]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}
